import React from "react";

/*
 * A small line icon per order status. Strokes use currentColor, so the caller sets the colour
 * (normally orderStatusColor from lib/orderStatusColors).
 */

type IconName =
	| "registered"
	| "clock"
	| "package"
	| "warehouse"
	| "truck"
	| "check"
	| "cross"
	| "return"
	| "hourglass"
	| "exchange"
	| "redirect"
	| "dot";

const statusIcon = (statusId: number): IconName => {
	switch (statusId) {
		case 1:
		case 18:
			return "registered";
		case 2:
		case 9:
			return "clock";
		case 3:
			return "package";
		case 4:
		case 144:
			return "warehouse";
		case 5:
		case 14:
			return "truck";
		case 6:
			return "check";
		case 7:
		case 17:
			return "cross";
		case 10:
		case 13:
		case 16:
		case 1410:
			return "return";
		case 8:
			return "hourglass";
		case 11:
		case 15:
			return "exchange";
		case 12:
			return "redirect";
		default:
			return "dot";
	}
};

const paths: Record<IconName, React.ReactNode> = {
	registered: (
		<>
			<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
			<path d="M14 3v5h5" />
			<path d="M12 11v6M9 14h6" />
		</>
	),
	clock: (
		<>
			<circle cx="12" cy="12" r="9" />
			<path d="M12 7v5l3 2" />
		</>
	),
	package: (
		<>
			<path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
			<path d="M3 8l9 5 9-5" />
			<path d="M12 13v8" />
		</>
	),
	warehouse: (
		<>
			<path d="M3 21V9l9-5 9 5v12" />
			<path d="M7 21v-8h10v8" />
			<path d="M7 17h10" />
		</>
	),
	truck: (
		<>
			<path d="M2 5h12v11H2z" />
			<path d="M14 9h4l4 4v3h-8" />
			<circle cx="6.5" cy="17.5" r="2" />
			<circle cx="17.5" cy="17.5" r="2" />
		</>
	),
	check: (
		<>
			<circle cx="12" cy="12" r="9" />
			<path d="M8 12.5l2.5 2.5L16 9.5" />
		</>
	),
	cross: (
		<>
			<circle cx="12" cy="12" r="9" />
			<path d="M9 9l6 6M15 9l-6 6" />
		</>
	),
	return: (
		<>
			<path d="M9 14L4 9l5-5" />
			<path d="M4 9h11a5 5 0 0 1 0 10h-3" />
		</>
	),
	hourglass: (
		<>
			<path d="M6 3h12M6 21h12" />
			<path d="M7 3v3a5 5 0 0 0 10 0V3" />
			<path d="M7 21v-3a5 5 0 0 1 10 0v3" />
		</>
	),
	exchange: (
		<>
			<path d="M17 3l4 4-4 4" />
			<path d="M3 11V9a2 2 0 0 1 2-2h16" />
			<path d="M7 21l-4-4 4-4" />
			<path d="M21 13v2a2 2 0 0 1-2 2H3" />
		</>
	),
	redirect: (
		<>
			<path d="M15 14l5-5-5-5" />
			<path d="M4 20v-7a4 4 0 0 1 4-4h12" />
		</>
	),
	dot: (
		<>
			<circle cx="12" cy="12" r="9" />
			<circle cx="12" cy="12" r="2" />
		</>
	),
};

const OrderStatusIcon = ({
	statusId,
	size = 16,
}: {
	statusId: number;
	size?: number;
}) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		{paths[statusIcon(statusId)]}
	</svg>
);

export default OrderStatusIcon;
